import { useCallback, useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { CalendarCheck, ChevronsUpDown, Globe, LayoutDashboard, ListChecks, LogOut, Menu, Moon, Package, Route as RouteIcon, Sun, Truck, Warehouse } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { useAuth } from '@/auth/useAuth';
import { useEventStream } from '@/api/realtime';
import { useOrdersDataStore } from '@/stores/ordersDataStore';
import { useTodayStore } from '@/stores/todayStore';
import { todayCount } from '@/features/today/todayLib';
import { MailPreviewDialog } from '@/features/mail-preview/MailPreviewDialog';
import { useTheme } from '@/lib/theme/useTheme';
import { FETCH_DOMAINS_PATH, NAV_ITEMS, VIEW_PERMISSIONS, WORK_GROUPS, viewCodes } from './navigation';

export const brandLockupClass = 'flex items-center gap-[9px] whitespace-nowrap font-display text-[19px] font-extrabold tracking-[-.03em] text-foreground';
const navRowClass = 'flex min-h-8 items-center justify-between gap-2 rounded-md px-2.5 py-1 text-[13px] text-foreground hover:bg-accent pointer-coarse:min-h-11';
const navRowActiveClass = 'bg-card font-semibold shadow-row in-data-[slot=sheet-content]:bg-accent in-data-[slot=sheet-content]:shadow-none';
// Every queue count wears the same quiet pill: colour in this app means a
// calendar kind or, in red, something late or wrong — never "this queue".
const navCountClass = 'inline-flex h-5 min-w-[22px] items-center justify-center rounded-full bg-secondary px-1.5 text-[11.5px] font-semibold tabular-nums text-muted-foreground';

/**
 * One navigation zone: the sidebar. The brand at its head; the pages
 * (Overview, Journeys, Packages, Warehouse) as top-level rows; a Work group listing
 * every queue with its count, on the page ground, so all of them are visible
 * at once without scrolling or hovering; the account and theme controls at
 * its foot. On small screens the same column is a sheet behind a slim bar.
 */
export function AppShell() {
  const { user, token, logout, can } = useAuth();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { theme, setThemeId } = useTheme();
  const { overview, releases, load, refreshIfStale } = useOrdersDataStore();
  // The Make-live count rides along with the overview, but only for users who
  // can open that queue — a count for a row they cannot see is noise.
  const withReleases = can(VIEW_PERMISSIONS.releases);
  useEffect(() => { void load({ withReleases }); }, [load, withReleases]);
  // Today's count sits on its row, so the shell keeps it as fresh as the queues.
  const withToday = viewCodes('today').every(can);
  const { today, load: loadToday, refreshIfStale: refreshTodayIfStale } = useTodayStore();
  useEffect(() => { if (withToday) void loadToday(); }, [loadToday, withToday]);
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      refreshIfStale(60_000);
      if (withToday) refreshTodayIfStale(60_000);
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => { window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, [refreshIfStale, withToday, refreshTodayIfStale]);
  const onRealtime = useCallback(() => { void load(); if (withToday) void loadToday(); }, [load, withToday, loadToday]);
  useEventStream(onRealtime, { authToken: token, enabled: can(VIEW_PERMISSIONS.shell) });
  const active = NAV_ITEMS.find(item => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`));
  // A row shows only when every code it needs is held — its page, and for
  // the money views the costs read — so a row never opens onto "No access".
  const visibleWork = NAV_ITEMS.filter(item => item.group === 'WORK' && item.codes.every(can));
  const close = () => setMobileNavOpen(false);
  const brand = <Link to="/" className={brandLockupClass} onClick={close}><Truck aria-hidden="true" className="size-[26px] text-brand-cherry" /><span>ACOT<span className="ml-[.28em]">Orders</span></span></Link>;
  const pages = [
    { to: '/', label: 'Overview', icon: LayoutDashboard, selected: location.pathname === '/', codes: viewCodes('overview') },
    { to: '/today', label: 'Today', icon: CalendarCheck, selected: active?.id === 'today', codes: viewCodes('today'), count: todayCount(today) },
    { to: '/journeys', label: 'Journeys', icon: RouteIcon, selected: active?.id === 'orders', codes: viewCodes('orders') },
    { to: '/packages', label: 'Packages', icon: Package, selected: active?.id === 'packages', codes: viewCodes('packages') },
    { to: '/warehouse', label: 'Warehouse', icon: Warehouse, selected: active?.id === 'warehouse', codes: viewCodes('warehouse') },
  ] as { to: string; label: string; icon: typeof LayoutDashboard; selected: boolean; codes: ReturnType<typeof viewCodes>; count?: number }[];
  const pageLinks = pages.filter(page => page.codes.every(can))
    .map(({ to, label, icon: Icon, selected, count }) => <Link key={to} to={to} onClick={close} className={cn(navRowClass, 'justify-start gap-2.5 text-[13.5px] font-medium', selected && navRowActiveClass)} aria-current={selected ? 'page' : undefined}><Icon aria-hidden="true" className={cn('size-4 shrink-0', selected ? 'text-primary' : 'text-muted-foreground')} /><span className="flex-1">{label}</span>{count != null && count > 0 && <span className={navCountClass}>{count.toLocaleString()}</span>}</Link>);
  const queues = WORK_GROUPS.map(group => {
    const items = group.ids.map(id => visibleWork.find(item => item.id === id)).filter(item => item != null);
    if (!items.length) return null;
    return <div key={group.label} className="flex flex-col gap-0.5"><div className="pb-1 pl-9 pr-2.5 pt-3.5 text-[11px] font-bold uppercase tracking-[.08em] text-muted-foreground">{group.label}</div>{items.map(item => {
      const count = overview && item.count?.({ work: overview.work, releases });
      const selected = active?.id === item.id;
      return <Link key={item.id} to={item.path} onClick={close} className={cn(navRowClass, 'pl-9', selected && navRowActiveClass)} aria-current={selected ? 'page' : undefined}>
        <span>{item.label}</span>{count != null && count > 0 && <span className={navCountClass}>{count.toLocaleString()}</span>}
      </Link>;
    })}</div>;
  });
  // Rendered twice — in the column on wide screens, in the sheet on phones —
  // so the two can never list different things.
  const sidebar = <>
    <div className="flex h-14 shrink-0 items-center pl-[17px] pr-3">{brand}</div>
    <nav className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 pb-4 pt-1" aria-label="Main navigation">
      <div className="flex flex-col gap-0.5">{pageLinks}</div>
      {visibleWork.length > 0 && <div><div className="flex items-center gap-2.5 px-2.5 pt-[22px] text-[13.5px] font-semibold text-foreground"><ListChecks aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" /><span>Work</span></div>{queues}</div>}
    </nav>
    <div className="flex shrink-0 items-center gap-2 border-t border-border pb-3 pl-3.5 pr-2.5 pt-2.5">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className="flex min-w-0 flex-1 items-center gap-2 rounded-md p-1 -m-1 text-left hover:bg-muted" aria-label="Account menu">
            <span className="grid size-[30px] shrink-0 place-items-center rounded-full bg-foreground text-[12px] font-bold text-background" aria-hidden="true">{user?.username.slice(0, 1).toUpperCase()}</span>
            <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{user?.username}</span>
            <ChevronsUpDown aria-hidden="true" className="size-3.5 shrink-0 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="start" className="w-52">
          <DropdownMenuItem asChild>
            <Link to={FETCH_DOMAINS_PATH} onClick={close}><Globe />Fetch domains</Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Button variant="ghost" size="icon" className="size-8 shrink-0" aria-label={theme.mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} onClick={() => setThemeId(theme.mode === 'dark' ? 'light' : 'dark')}>{theme.mode === 'dark' ? <Sun /> : <Moon />}</Button>
      <Button variant="ghost" size="icon" className="size-8 shrink-0" aria-label="Sign out" onClick={logout}><LogOut /></Button>
    </div>
  </>;
  return <div className="flex h-dvh flex-col bg-background">
    <a href="#main-content" className="fixed left-4 top-[-100px] z-[100] bg-card p-3 focus:top-3">Skip to content</a>
    <header className="flex h-[52px] shrink-0 items-center gap-1.5 border-b border-border bg-card px-3 md:hidden">
      <Button variant="ghost" size="icon" aria-label="Open navigation" onClick={() => setMobileNavOpen(true)}><Menu /></Button>
      {brand}
    </header>
    <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}><SheetContent side="left" className="w-72 gap-0 p-0"><SheetTitle className="sr-only">Navigation</SheetTitle>{sidebar}</SheetContent></Sheet>
    <div className="flex min-h-0 flex-1">
      <aside className="hidden min-h-0 w-60 shrink-0 flex-col border-r border-border md:flex">{sidebar}</aside>
      <main id="main-content" className="min-h-0 min-w-0 flex-1 overflow-hidden"><Outlet /></main>
    </div>
    <MailPreviewDialog />
  </div>;
}
